import { logCrashlyticsMessage, recordCrashlyticsError } from './analytics';

/**
 * [QHDEV-LOGGING] Single entry point for app logging.
 *
 * The rules this module exists to enforce:
 *
 * 1. `console.*` is a development tool. `installProductionConsoleGuard` silences it
 *    in release builds so nothing we print ends up in a shipped device's log. Before
 *    this existed, ~317 console statements shipped to production, including the Zoom
 *    session JWT (see the note in src/api/videoToken.ts).
 * 2. Anything worth keeping in production goes through `logEvent` / `logError`, which
 *    route to Crashlytics as breadcrumbs and non-fatal reports. Those are attached to
 *    any crash that follows and are not visible on the device.
 * 3. CloudWatch lines do NOT come from here. The app cannot write to CloudWatch. Call
 *    lifecycle appears there because the app calls the video-session endpoints and the
 *    API logs them server-side -- see src/api/videoSession.ts.
 *
 * Never pass PHI or credentials in `data`. `redact` catches the common key names as a
 * backstop, but it is a safety net, not a licence to log patient data.
 */

/** Keys whose values are never safe to log, at any nesting depth. */
const REDACTED_KEYS = [
  'accesstoken',
  'authorization',
  'jwt',
  'password',
  'pin',
  'refreshtoken',
  'secret',
  'signature',
  'token'
];

const REDACTED = '[redacted]';

/** Guards against cycles and runaway objects in logged payloads. */
const MAX_DEPTH = 4;

export type LogData = Record<string, unknown>;

/**
 * Shared tags, so a search for one subsystem finds every line it emitted. Grep
 * `[video-call]` in Metro or Crashlytics to follow a call end to end.
 */
export const VIDEO_CALL_TAG = 'video-call';

/**
 * Replaces credential-shaped values with a placeholder. Applied to every payload so a
 * careless call site cannot leak a token the way `videoToken.ts` used to.
 */
export const redact = (value: unknown, depth = 0): unknown => {
  if (value == null || depth > MAX_DEPTH) return value;
  if (Array.isArray(value)) return value.map(item => redact(item, depth + 1));
  if (typeof value !== 'object') return value;

  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    result[key] = REDACTED_KEYS.includes(key.toLowerCase())
      ? REDACTED
      : redact(entry, depth + 1);
  }
  return result;
};

/** `[tag] message {json}` -- one line, greppable by tag in Metro and in Crashlytics. */
const format = (tag: string, message: string, data?: LogData): string => {
  if (!data) return `[${tag}] ${message}`;
  try {
    return `[${tag}] ${message} ${JSON.stringify(redact(data))}`;
  } catch {
    // Circular or otherwise unserialisable payload: the message still has value.
    return `[${tag}] ${message} [unserializable payload]`;
  }
};

/**
 * Development-only. Use for the noisy detail that helps while building a feature and
 * has no business surviving into a release build.
 */
export const logDebug = (
  tag: string,
  message: string,
  data?: LogData
): void => {
  if (!__DEV__) return;
  // eslint-disable-next-line no-console
  console.log(format(tag, message, data));
};

/**
 * A thing that happened and is worth having in a crash report: call joined, call left,
 * session ended. Console in development, Crashlytics breadcrumb in production.
 */
export const logEvent = (
  tag: string,
  message: string,
  data?: LogData
): void => {
  const line = format(tag, message, data);
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.log(line);
  }
  logCrashlyticsMessage(line);
};

/**
 * A failure. Recorded as a non-fatal in Crashlytics so it is searchable and grouped
 * even when the app does not crash -- which is the case for every swallowed
 * `.catch(() => {})` this codebase is full of.
 */
export const logError = (tag: string, error: unknown, data?: LogData): void => {
  const normalized =
    error instanceof Error
      ? error
      : new Error(String(error ?? 'Unknown error'));
  const line = format(tag, normalized.message, data);
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.warn(line, normalized);
  }
  logCrashlyticsMessage(line);
  recordCrashlyticsError(normalized);
};

let consoleGuardInstalled = false;

/**
 * Silences `console.*` in release builds and routes `console.error` into Crashlytics
 * so genuine failures are not simply dropped on the floor.
 *
 * Call once, as early as possible. No-op in development, so Metro keeps working
 * exactly as before.
 */
export const installProductionConsoleGuard = (): void => {
  if (__DEV__ || consoleGuardInstalled) return;
  consoleGuardInstalled = true;

  const noop = () => undefined;
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.warn = noop;

  // Keep errors, but send them somewhere useful instead of the device log.
  console.error = (...args: unknown[]) => {
    const first = args[0];
    const normalized =
      first instanceof Error ? first : new Error(args.map(String).join(' '));
    logCrashlyticsMessage(`[console.error] ${normalized.message}`);
    recordCrashlyticsError(normalized);
  };
};
